import { TOOL_MEMBERS, TOOL_RESOLVE, TOOL_SEARCH_ANALYST, TOOL_RUN_AGENT, ANALYST_TOOLS } from "./LLMClient";
import { Fragment, useEffect, useState } from "react";
import { blunt, extractSearchParams, findRealRunSearchToolUse, toolIds } from "../tools";
import { LabeledSubmitSearchButton } from "../components/SubmitSearchButton";

import { renderLLMMarkdown } from "./renderLLMMarkdown";
import { TS } from "../lib/utils";

export const ToolUseBlock = ({
                               className = "tool-use",
                               agentId = null,
                               toolUse,
                               agentMessages = null,
                               trapErrors = false,
                               showAdvancedFeatures = false,
                               isRetry = false,
                             }) => {
  // Until the toolUse message block is complete, the input is a string
  const {
    name,
    toolUseId,
    input
  } = toolUse;
  const toolInputReady = typeof (input) === "object";
  // The analyst's own run_search leaf call (once it streams in) carries the real,
  // already-expand_members-resolved ids/params — prefer it over the outer orchestration
  // call's input, which may name a geneset/cpdset rather than what was actually searched.
  const realRunSearch = name === TOOL_SEARCH_ANALYST ? findRealRunSearchToolUse(agentMessages) : null;
  const effectiveInput = realRunSearch?.input ?? input;
  const [searchParams, setSearchParams] = useState(null);

  useEffect(() => {
    if (typeof (effectiveInput) === "object") {
      const dsConfig = 0;
      setSearchParams(extractSearchParams(effectiveInput, dsConfig));
    }
  }, [effectiveInput]);

  const stringInput = toolInputReady ? JSON.stringify(input) : input;

  if (name === TOOL_RESOLVE || name === TOOL_MEMBERS) {
    return showAdvancedFeatures && (
      <div title="The guide is requesting these terms be resolved into Plex IDs"
           className={`${className} tool-use power-user`}>
        <em>{name}</em> request {stringInput}
      </div>
    );
  }
  if (name === TOOL_SEARCH_ANALYST) {
    const instructions = blunt(input?.instructions || "");
    const markdown = renderLLMMarkdown(instructions, trapErrors);
    // A list even when the model sent the ids JSON-encoded (PLEX-6670)
    const searchIds = typeof (effectiveInput) === "object" ? toolIds(effectiveInput?.ids) : [];
    const hasSearchButton = searchIds.length > 0 && !!searchParams;

    // Return null if nothing would be visible
    if (!showAdvancedFeatures && !hasSearchButton) {
      return null;
    }

    return (
      <Fragment>
        {showAdvancedFeatures && (
          <div key={`tool-info-${toolUseId}`}
               title="Details of the tool use requested by the guide"
               className={`${className} tool-use power-user`}>
            <em>{name}</em> {stringInput}
          </div>
        )}
        {showAdvancedFeatures && (
          <div key={`tool-use-text-${toolUseId}`}
               title="Instructions from the guide to a sub-agent"
               className={`${className} tool-use tool-use-text power-user`}>
            {markdown}
          </div>
        )}
        {hasSearchButton && (
          <Fragment key={`search-button-${toolUseId}`}>
            <LabeledSubmitSearchButton
              className={`button btn`}
              searchParams={searchParams}
              title={`Click to view this search's results in detail`}
              ids={searchIds}
            />
            {isRetry && (
              <span className="search-retry-tag" title="This search repeats a prior search's ids within this turn">
                (retry)
              </span>
            )}
          </Fragment>)}
      </Fragment>
    );
  } else if (ANALYST_TOOLS.has(name)) {
    // Analyst tool use — show compact progress label for all users
    const ANALYST_TOOL_LABELS = {
      fetch_totals:    "Counting results…",
      fetch_results:   "Fetching results…",
      fetch_entities:  "Loading details…",
      fetch_neighbors: "Exploring connections…",
    };
    const label = ANALYST_TOOL_LABELS[name] ?? `${name}…`;
    return (
      <div className={`${className} tool-use analyst-tool`} title={showAdvancedFeatures ? stringInput : undefined}>
        <em>{label}</em>
      </div>
    );
  } else if (name === TOOL_RUN_AGENT) {
    // Dispatches a long-running (multi-minute) flavor agent — without a progress label
    // here, and without the accompanying Turn.js visibility fix, the whole operation
    // looked frozen to the user (WEBAPP-B0/B1).
    const agentType = toolInputReady && input?.agent_type;
    const objective = toolInputReady && blunt(input?.objective || "");
    return (
      <div className={`${className} tool-use analyst-tool`}
           title={showAdvancedFeatures ? stringInput : (objective || undefined)}>
        <em>Investigating{agentType ? ` (${agentType})` : ""}{objective ? `: ${objective}` : "…"}</em>
      </div>
    );
  } else {
    // Miscellaneous agent tool use
    return showAdvancedFeatures && (
      <div className={`${className} tool-use power-user`}>
        <em>{name}</em> request ${stringInput})
      </div>
    );
  }
};

